import { Injectable } from '@angular/core';
import { Observable, catchError, combineLatest, map, of, tap, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	ApolloQueryError,
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	CrossPointUpdateInput,
	NodeChangeType,
	SubscriptionManagerService
} from '@shure/shared/angular/data-access/system-api/core';
import { SignalType, Response, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers } from '@shure/shared/typescript/rxjs';

import { SignalGeneratorApiService, SignalGeneratorChannel, SignalTarget } from '../../api';
import {
	SignalGeneratorChannelMutationGQL,
	SignalGeneratorChannelQueryGQL,
	SignalGeneratorChannelSubscriptionGQL,
	SignalGeneratorCrossPointSubscriptionGQL,
	SignalGeneratorSignalTargetMutationGQL,
	SignalGeneratorSignalTargetQueryGQL,
	SignalGeneratorSignalTargetSubscriptionGQL
} from '../graphql/generated/signal-generator.generated';
import { mapSignalGeneratorChannelFromSysApi, mapSignalGeneratorSignalTargetFromSysApi } from '../mappers';

@Injectable()
export class DaiDeviceUiDspSignalGeneratorService implements SignalGeneratorApiService {
	private readonly logger: ILogger;

	private readonly signalGeneratorChannelSubscriptionManager: CombiningSubscriptionManager;
	private readonly signalGeneratorCrossPointSubscriptionManager: CombiningSubscriptionManager;
	private readonly signalGeneratorTargetChannelSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly signalGeneratorChannelQueryGQL: SignalGeneratorChannelQueryGQL,
		private readonly signalGeneratorChannelMutationGQL: SignalGeneratorChannelMutationGQL,
		private readonly signalGeneratorSignalTargetQueryGQL: SignalGeneratorSignalTargetQueryGQL,
		private readonly signalGeneratorSignalTargetMutationGQL: SignalGeneratorSignalTargetMutationGQL,
		signalGeneratorSignalTargetSubscriptionGQL: SignalGeneratorSignalTargetSubscriptionGQL,
		signalGeneratorChannelSubscriptionGQL: SignalGeneratorChannelSubscriptionGQL,
		signalGeneratorCrossPointSubscriptionGQL: SignalGeneratorCrossPointSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('DaiDeviceUiDspSignalGeneratorService');

		this.signalGeneratorChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspSignalGeneratorService',
				changeTypes: [
					NodeChangeType.AudioChannelSignalGenerator,
					NodeChangeType.AudioChannelName,
					NodeChangeType.AudioChannelChannelConfiguration
				],
				subscriptionGql: signalGeneratorChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);

		this.signalGeneratorCrossPointSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspSignalGeneratorService',
				changeTypes: [NodeChangeType.CrossPointConfiguration],
				subscriptionGql: signalGeneratorCrossPointSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);

		this.signalGeneratorTargetChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspSignalGeneratorService',
				changeTypes: [NodeChangeType.AudioChannelName, NodeChangeType.AudioChannelChannelConfiguration],
				subscriptionGql: signalGeneratorSignalTargetSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getChannel$(channelId: string): Observable<SignalGeneratorChannel> {
		const queryId = uuidv4();
		this.logger.trace('getChannel$()', 'Querying signal generator channel', { channelId, queryId });

		return this.signalGeneratorChannelQueryGQL
			.watch({ channelId }, { fetchPolicy: 'network-only' })
			.valueChanges.pipe(
				map((query) => {
					if (query.data?.node && 'features' in query.data.node) {
						this.logger.trace('getChannel$()', 'Query updated', { channelId });
						return mapSignalGeneratorChannelFromSysApi(query.data.node);
					}

					this.logger.error('getChannel$()', 'Failed to query channel', { channelId });
					throw ApolloQueryErrorMapper.getError(query);
				}),
				tap((channel) => this.signalGeneratorChannelSubscriptionManager.subscribe(queryId, channel.id)),
				noMoreSubscribers(() => {
					this.logger.trace('getChannel$', 'noMoreSubscribers', { channelId, queryId });
					this.signalGeneratorChannelSubscriptionManager.unsubscribeQuery(queryId);
				}),
				catchError((error) => {
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	public getSignalTargets$(channelIds: string[]): Observable<SignalTarget[]> {
		const queryId = uuidv4();
		this.logger.trace('getSignalTargets$()', 'Querying signal generator targets', { channelIds, queryId });

		return combineLatest(
			channelIds.map((channelId) => {
				let targetId: string;
				return this.signalGeneratorSignalTargetQueryGQL.watch({ channelId }).valueChanges.pipe(
					map((query) => {
						if ('id' in query.data.node) {
							this.logger.trace('getSignalTargets$()', 'Query updated', { channelId });
							return mapSignalGeneratorSignalTargetFromSysApi(query.data.node);
						}

						this.logger.error('getSignalTargets$()', 'Failed to query signal generator targets', {
							channelId
						});
						throw query.error;
					}),
					tap((target) => {
						targetId = target.id;
						this.signalGeneratorCrossPointSubscriptionManager.subscribe(queryId, targetId);
						this.signalGeneratorTargetChannelSubscriptionManager.subscribe(queryId, channelId);
					}),
					noMoreSubscribers(() => {
						this.logger.trace('getSignalTargets$', 'noMoreSubscribers', { targetId, queryId });
						this.signalGeneratorCrossPointSubscriptionManager.unsubscribeQuery(queryId);
						this.signalGeneratorTargetChannelSubscriptionManager.unsubscribeQuery(queryId);
					}),
					catchError((error) => {
						return throwError(() => new ApolloQueryError(error));
					})
				);
			})
		);
	}

	public toggleSignalEnabled(channelId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleSignalEnabled()', 'Toggling signal enabled', { channelId });
		return this.updateChannel(channelId, { enabled });
	}

	public setSignalType(channelId: string, signalType: SignalType): Observable<Response<void, string>> {
		this.logger.trace('setSignalType()', 'Setting signal type', { channelId });
		return this.updateChannel(channelId, { signalType });
	}

	public setGain(channelId: string, gain: number): Observable<Response<void, string>> {
		this.logger.trace('setGain()', 'Setting signal generator gain', { channelId });
		return this.updateChannel(channelId, { gain });
	}

	public setFrequency(channelId: string, frequency: number): Observable<Response<void, string>> {
		this.logger.trace('setFrequency()', 'Setting signal generator frequency', { channelId });
		return this.updateChannel(channelId, { frequency });
	}

	public setSignalTarget(crossPointId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('setSignalTarget()', 'Setting signal generator target crosspoint', { crossPointId });
		return this.updateCrossPoint({
			id: crossPointId,
			features: {
				configuration: {
					enabled
				}
			}
		});
	}

	private updateChannel(
		channelId: string,
		update: Partial<SignalGeneratorChannel['features']['signalGenerator']>
	): Observable<Response<void, string>> {
		return this.signalGeneratorChannelMutationGQL
			.mutate(
				{
					updates: {
						audioChannel: {
							id: channelId,
							features: {
								signalGenerator: {
									...update
								}
							}
						}
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateNodes &&
						'error' in mutation.data.updateNodes[0] &&
						mutation.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateChannel()', 'Failed to update channel', {
							channelId,
							mutation
						});
						return { state: ResponseState.Error, error: mutation.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateChannel()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error) => {
					this.logger.error('updateChannel()', 'Failed to update channel', { error });
					return of({ state: ResponseState.Error });
				})
			);
	}

	private updateCrossPoint(update: Partial<CrossPointUpdateInput>): Observable<Response<void, string>> {
		if (!update.id) {
			return of({ state: ResponseState.Error, error: 'no id passed' });
		}
		return this.signalGeneratorSignalTargetMutationGQL
			.mutate(
				{
					updates: {
						crossPoint: {
							id: update.id,
							features: update.features
						}
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateChannel()', 'Failed to update matrix mixer channel', {
							result
						});
						return { state: ResponseState.Error, error: result.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateChannel()', 'Done', { result });
					return { state: ResponseState.Done };
				})
			);
	}
}
